/**
 * Authentication Context
 * Wraps Clerk authentication and provides a consistent interface
 */

import React, { createContext, useContext, useEffect } from 'react';
import { useUser, useAuth as useClerkAuth, useClerk } from '@clerk/react-router';
import { identifyUser, resetUser } from '@/lib/analytics';
import { setAuthTokenGetter } from '@/lib/api-client';

interface User {
  id: string;
  email: string;
  companyName: string;
  role: string;
  isVerified: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, companyName: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { user: clerkUser, isLoaded: clerkLoaded } = useUser();
  const { signOut, getToken } = useClerkAuth();
  const { redirectToSignIn } = useClerk();

  // Inject Clerk's getToken into the API client synchronously.
  // This must NOT be in a useEffect — React Query starts fetching during render
  // (before effects commit), so the token getter must be set before that happens.
  setAuthTokenGetter(getToken);

  // Map Clerk user to our User interface
  const user: User | null = clerkUser
    ? {
        id: clerkUser.id,
        email: clerkUser.emailAddresses[0]?.emailAddress || '',
        companyName: clerkUser.organizationMemberships[0]?.organization.name || '',
        role: clerkUser.organizationMemberships[0]?.role || 'member',
        isVerified: clerkUser.emailAddresses[0]?.verification?.status === 'verified',
      }
    : null;

  // Identify user in analytics when they sign in
  React.useEffect(() => {
    if (user) {
      try {
        identifyUser(user.id, {
          email: user.email,
          role: user.role,
          company_name: user.companyName,
          is_verified: user.isVerified,
        });
      } catch (analyticsError) {
        console.error('Failed to identify user in analytics:', analyticsError);
      }
    }
  }, [user]);

  /**
   * Login - redirects to Clerk sign-in
   */
  const login = async (_email: string, _password: string) => {
    // Clerk handles login through its UI
    redirectToSignIn();
  };

  /**
   * Register - redirects to Clerk sign-up
   */
  const register = async (_email: string, _password: string, _companyName: string) => {
    // Clerk handles registration through its UI
    window.location.href = '/sign-up';
  };

  /**
   * Logout user
   */
  const logout = async () => {
    resetUser(); // Clear analytics user identity
    await signOut();
    window.location.href = '/sign-in';
  };

  /**
   * Refresh user data (no-op with Clerk, it auto-syncs)
   */
  const refreshUser = async () => {
    // Clerk automatically keeps user data in sync
  };

  const value: AuthContextType = {
    user,
    isLoading: !clerkLoaded,
    isAuthenticated: !!clerkUser,
    login,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
